/* eslint-disable max-len */
import { useMemo, useRef, useState } from "react";
import {
  Show,
  SimpleShowLayout,
  TextField,
  ImageField,
  useShowContext,
  useTranslate,
  EmailField,
  DateField,
  Labeled,
  FunctionField,
  useNotify,
  useGetList
} from "react-admin";
import { useQuery } from "@apollo/client";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  AppointmentDetailsIcon,
  EditButtonIcon,
  PersonIcon,
  TransactionsIcon
} from "assets/Icons";
import CustomHeaderActions from "./CustomHeaderActions";
import * as Axios from "../providers/axios";
import * as User from "../providers/user";
import { useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { muiColors } from "themes/colors";
import UserAppointmentsList from "./UserAppointmentsList";
import ShowCards from "./ShowCards";
import { CustomButton } from "./CustomButton";
import OrderCreateModal from "./OrderCreateModal";
import UserRefundList from "./UserRefundList";
import { Link } from "react-router-dom";
import { GET_USER } from "../gql";
import { getPhone } from "./PhoneInput";
import OrderTransactionsList from "./OrderTransactionsList";
import NotificationSettingsShow from "./NotificationSettingsShow";

const PhotoField = () => {
  const { record } = useShowContext();

  if (record?.photo_url) {
    return (
      <ImageField
        source="photo_url"
        sx={{
          "& > img": {
            width: "40px !important",
            height: "40px !important",
            borderRadius: "20px",
            objectFit: "cover !important",
            margin: "0px 0px 6px 0px !important"
          }
        }}
      />
    );
  }
  return null;
};

const ConditionalTextField = ({
  sourceName,
  setRoleRestricted,
  permissions
}: {
  sourceName: string;
  setRoleRestricted?: (bool: boolean) => void;
  permissions?: any;
}) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (
    !permissions.isSuperAdmin &&
    sourceName === "role" &&
    record?.[sourceName] &&
    record?.joined_company_id !== permissions.companyId
  ) {
    return null;
  }
  if (sourceName === "birthday" && record?.[sourceName]) {
    return (
      <Labeled label={translate(`users.${sourceName}`)}>
        <DateField source={sourceName} />
      </Labeled>
    );
  }

  if (setRoleRestricted && permissions) {
    if (permissions.isArtist || permissions.isUser) {
      setRoleRestricted(true);
    }
  }

  if (record?.[sourceName]) {
    return (
      <Labeled label={translate(`users.${sourceName}`)}>
        <TextField source={sourceName === "role" ? "role.role" : sourceName} />
      </Labeled>
    );
  }

  return null;
};

const LoginAsCustomerButton = ({ locationSlug }: { locationSlug?: string }) => {
  const { api } = Axios.useAxios();
  const { id } = useParams<{ id: string }>();
  const notify = useNotify();

  const handleImpersonate = async () => {
    try {
      const { data } = await api.post("v1/users/impersonate-login", {
        userId: id
      });

      const redirectUrl = `${process.env.REACT_APP_CUSTOMER_APP_URL}/company/${data?.companySlug}`;

      window.location.href = locationSlug
        ? `${redirectUrl}/location/${locationSlug}`
        : redirectUrl;
    } catch (error) {
      notify(error.response.data.message, {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "top", horizontal: "center" }
      });
    }
  };

  return (
    <CustomButton
      onClick={handleImpersonate}
      text="Schedule a Pointment"
      cyId="schedule-appointment-button"
      sx={{
        "@media screen and (max-width:640px)": {
          width: "100%"
        }
      }}
    />
  );
};

export default function UserShow() {
  const translate = useTranslate();
  const { user, permissions } = User.useUser();
  const { id } = useParams<{ id: string }>();
  const [roleRestricted, setRoleRestricted] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const router = useLocation();
  const resourceFromUrl = router.pathname.split("/")[2];
  const transactionsSectionRef = useRef<HTMLDivElement>(null);

  const { data: customerUser, refetch } = useQuery(GET_USER, {
    variables: {
      where: { id }
    }
  });

  const isOwnAccount = !!user?.id && id === user.id;
  const showReminders =
    isOwnAccount || permissions.isSuperAdmin || permissions.isOwner;
  const showPromo = isOwnAccount || permissions.isSuperAdmin;

  const customerUserId = customerUser?.user?.id;
  const { data: transactionsData } = useGetList(
    "Transaction",
    {
      filter:
        permissions?.companyId && customerUserId
          ? {
              user_id: customerUserId,
              payment_method: "STRIPE",
              status: "SUCCESS",
              order: {
                is: {
                  location: {
                    is: { company_id: permissions.companyId }
                  }
                }
              }
            }
          : undefined,
      pagination: { page: 1, perPage: 1 }
    },
    { enabled: Boolean(permissions?.companyId && customerUserId) }
  );
  const hasRefundableTransaction = (transactionsData?.length ?? 0) > 0;

  const handleScrollToTransactions = () => {
    transactionsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  const location: { id?: string; slug?: string } = useMemo(() => {
    if (user && user?.company?.locations?.length === 1) {
      return {
        id: user.company.locations[0].id as string,
        slug: user.company.locations[0].slug as string
      };
    }
    return {
      id: undefined,
      slug: undefined
    };
  }, [user]);

  if (permissions.isCompanyLimited) {
    window.location.href = "/dashboard";
    return null;
  }

  return (
    <Show
      resource="User"
      actions={<CustomHeaderActions title={translate("users.customer")} />}
      sx={{
        "& .RaShow-card": {
          backgroundColor: muiColors.bg.default
        },
        "& .RaDatagrid-headerCell": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          paddingTop: "16px",
          paddingBottom: "16px",
          lineHeight: "18px"
        },
        "& .RaDatagrid-rowCell": {
          borderBottom: "none"
        }
      }}
    >
      <div className="w-full">
        <div className="h-min w bg-mono-primaryLightBgColor rounded-[8px] mb-4">
          <SimpleShowLayout>
            <ShowAndEditDetails
              title={translate("users.customer_details")}
              view="show"
              icon={<PersonIcon />}
              customButtons={
                <div className="flex gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
                  {!roleRestricted && id !== user.id && (
                    <LoginAsCustomerButton locationSlug={location.slug} />
                  )}
                  <CustomButton
                    onClick={() => setIsCreateOrderModalOpen(true)}
                    text={translate("users.create_an_order")}
                    sx={{
                      "@media screen and (max-width:640px)": {
                        width: "100%"
                      }
                    }}
                  />
                  {hasRefundableTransaction && (
                    <CustomButton
                      onClick={handleScrollToTransactions}
                      text="Refund"
                      variant="secondary"
                      sx={{
                        "@media screen and (max-width:640px)": {
                          width: "100%"
                        }
                      }}
                    />
                  )}
                  {isCreateOrderModalOpen && (
                    <OrderCreateModal
                      isOpen={!location.id && isCreateOrderModalOpen}
                      setIsOpen={setIsCreateOrderModalOpen}
                      locationId={location.id}
                      customerUserId={
                        customerUser?.user?.impersonate_id ||
                        customerUser?.user?.id
                      }
                    />
                  )}
                  <Link
                    to={`/dashboard/${resourceFromUrl}/${customerUser?.user?.id}`}
                    style={{ width: "inherit" }}
                  >
                    <CustomButton
                      text={translate("common.edit")}
                      variant="secondary"
                      startIcon={<EditButtonIcon />}
                      sx={{
                        "@media screen and (max-width:640px)": {
                          width: "100%"
                        }
                      }}
                    />
                  </Link>
                </div>
              }
            />
            <ConditionalTextField
              sourceName="role"
              setRoleRestricted={setRoleRestricted}
              permissions={permissions}
            />
            <PhotoField />
            <FunctionField
              label={translate("users.customer") + ":"}
              render={(record: any) => (
                <div className="flex">
                  <div className="line-clamp-1 break-all text-[16px] text-mono-primaryTextColor font-[400] font-['Inter']">
                    {record?.first_name + " " + record?.last_name}
                  </div>
                </div>
              )}
            />
            <EmailField source="email" label={translate("users.email") + ":"} />
            <FunctionField
              label={translate("users.phone_number") + ":"}
              render={(record: any) => (
                <div className="text-[16px] text-[#16667A] font-[400] font-['Inter']">
                  {getPhone(record?.libphonenumber) || "-"}
                </div>
              )}
            />
            <FunctionField
              label={translate("users.birthday") + ":"}
              render={(record: any) => (
                <div className="text-[16px] text-mono-primaryTextColor font-[400] font-['Inter']">
                  {record?.birthday
                    ? dayjs(record?.birthday)?.format("MMM DD")
                    : "-"}
                </div>
              )}
            />
            <FunctionField
              label={translate("users.pronoun") + ":"}
              render={(record: any) => (
                <div className="text-[16px] text-mono-primaryTextColor font-[400] font-['Inter']">
                  {record?.pronoun || "-"}
                </div>
              )}
            />
            <NotificationSettingsShow
              user={customerUser?.user}
              showReminders={showReminders}
              showPromo={showPromo}
            />
            <FunctionField
              label={translate("users.description") + ":"}
              render={(record: any) => {
                const companyDescriptions = record?.company_descriptions?.find(
                  (cd: any) => cd.company_id === permissions?.companyId
                );
                return (
                  <div className="text-[16px] text-mono-primaryTextColor font-[400] font-['Inter']">
                    {companyDescriptions?.description || "-"}
                  </div>
                );
              }}
            />
            <ShowCards user={customerUser?.user} refetch={refetch} />
            {/* <ConditionalTextField sourceName="stripe_customer_id" /> */}
          </SimpleShowLayout>
        </div>
        <div className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2 px-4 mb-4">
          <ShowAndEditDetails
            title={translate("users.refunds")}
            view="show"
            icon={<AppointmentDetailsIcon />}
            hasEdit={false}
            marginBottom="0px"
            borderBottom="none"
          />
          <UserRefundList />
        </div>
        <div className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2 px-4">
          <ShowAndEditDetails
            title={translate("users.appointments")}
            view="show"
            icon={<AppointmentDetailsIcon />}
            hasEdit={false}
            marginBottom="0px"
            borderBottom="none"
          />
          <UserAppointmentsList />
        </div>
        <div
          ref={transactionsSectionRef}
          className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2 px-4 my-4 scroll-mt-4"
        >
          <ShowAndEditDetails
            title={translate("order.transactions")}
            view="show"
            icon={<TransactionsIcon />}
            hasEdit={false}
            marginBottom="0px"
            borderBottom="none"
          />
          <OrderTransactionsList
            userCompanyId={permissions?.companyId}
            customerUserId={customerUser?.user?.id}
          />
        </div>
      </div>
    </Show>
  );
}
